import { IPatientPurchase } from './patientPurchase';
import { IService } from './service';

/**
 * An appointment: booked or walked in, held or missed, billable or free.
 *
 * One shape for the whole diary. The therapist's day, a patient's upcoming list and a
 * purchase's history are all lists of these, which is why none of them needs its own
 * model — and why a no-show shows up in a patient's history rather than quietly not
 * existing.
 */

/**
 * Where an appointment stands.
 *
 * Only `HELD` consumes a session and earns the clinic anything. A `NO_SHOW` deliberately
 * costs the patient nothing — they keep the session and the clinic counts the miss
 * instead — so a card showing "2 left" after a missed appointment is correct, not stale.
 *
 * `REQUESTED` is a patient's own booking waiting for its therapist: it holds the hour and
 * the session exactly as `SCHEDULED` does, and becomes `SCHEDULED` when that therapist
 * confirms it. `DECLINED` is the other way out — the therapist said no, or nobody answered
 * three hours before it starts — and is final: it frees both and is never reopened.
 */
export type AppointmentStatus =
    | 'SCHEDULED'
    | 'HELD'
    | 'NO_SHOW'
    | 'CANCELLED'
    | 'REQUESTED'
    | 'DECLINED';

/**
 * Which half of a history a page of it comes from.
 *
 * `UPCOMING` is still going to happen -- not yet started and still unresolved -- and
 * `PAST` is everything else. The two are complementary rather than merely adjacent, so
 * every appointment is in exactly one of them and none falls between.
 *
 * The split is the server's rather than this side's, and that is the point: it used to be
 * an `Array.filter` comparing `new Date().toISOString()` against `startsAt`, which is UTC
 * against the clinic's zone-less wall clock -- so a row near the boundary landed in the
 * wrong section by the viewer's offset. The API answers from the clinic's own clock.
 */
export type AppointmentScope = 'UPCOMING' | 'PAST';

export interface IAppointment {
    id: number;
    /** Absent on a block of time, which belongs to no patient. */
    patientId?: number | null;
    patientName?: string | null;
    therapistId: number;
    therapistName: string;
    /** Absent on a block of time, and when the patient has set no picture. */
    patientPictureUrl?: string | null;
    /** Absent when the therapist has set no picture. */
    therapistPictureUrl?: string | null;
    /** Absent when this draws down nothing: a free evaluation, or a session not yet paid for. */
    purchaseId?: number | null;
    /**
     * What the appointment is for, in both languages, as the catalogue read at booking time.
     *
     * Unresolved on purpose, like `/service-catalog`'s own `nameRo`/`nameEn`: the pair travels
     * and the reader picks, through `localised()`. Both absent on a block of time.
     *
     * The Romanian half is also the stable identity — it is what a purchase snapshots and what
     * the invoice bills under — so anything *matching* a service rather than showing one
     * compares this one, never the localised result.
     */
    serviceNameRo?: string | null;
    /** Absent whenever the clinic gave the service no English name, which is usual. */
    serviceNameEn?: string | null;
    /**
     * Local wall-clock, no zone, e.g. `2026-03-10T09:00:00` — exactly the hour the clinic
     * means. Never put either of these through anything that would shift them by the
     * viewer's offset.
     */
    startsAt: string;
    endsAt: string;
    status: AppointmentStatus;
    /** True when this is leave or a room closure rather than somebody's appointment. */
    timeBlock: boolean;
    notes?: string | null;
    /**
     * The last moment a `REQUESTED` appointment can be confirmed — three hours before it
     * starts. Null on every other status. Wall-clock, like `startsAt`, and computed by the
     * API so this side never restates the rule.
     */
    confirmBy?: string | null;
    /** What the therapist wrote when they declined. Only ever on a `DECLINED` row, and optional there. */
    decisionNote?: string | null;
    /** True on a `DECLINED` row nobody declined: the request lapsed unanswered. */
    declinedAutomatically?: boolean;
}

/**
 * What POST /appointments takes.
 *
 * The same request books, records a walk-in and blocks out time — they differ only in
 * what they leave out. A patient booking for themselves has `patientId` ignored: the
 * server takes it from their token, so nobody can book in somebody else's name.
 */
export interface CreateAppointmentRequest {
    /** Omit entirely to block out time. Ignored when a patient books for themselves. */
    patientId?: number;
    therapistId: number;
    /** The package this comes out of. Omit for a free evaluation or a pay-after session. */
    purchaseId?: number;
    /** The catalogue option. Required when there is no purchase and this is not a block. */
    serviceId?: number;
    startsAt: string;
    endsAt: string;
    /**
     * Defaults to SCHEDULED. Pass HELD to record something that already happened. Ignored
     * when a patient books for themselves: theirs is always a `REQUESTED` booking.
     */
    status?: AppointmentStatus;
    notes?: string;
}

/**
 * What may pay for one session, asked as its card is opened.
 *
 * A read of its own rather than fields on `IAppointment`, and the split is made on the
 * server because the browser cannot make it: a plan's discipline is its
 * `sourceCatalogOptionId` resolved up to a catalogue type, and `GET /services` answers
 * with singles only — a package's own option is never in the list this app holds, so there
 * is no way here to tell a Kinetoterapie plan from a Masaj one. Filtering on what *is*
 * held is how a package for another discipline came to be the only thing the picker
 * offered.
 */
export interface IAppointmentPaymentOptions {
    /** Plans of this appointment's own discipline. What to reach for first. */
    matching: IPatientPurchase[];
    /**
     * Usable plans for another discipline. Offered under a heading of their own rather
     * than hidden — spending one on purpose is ordinary, and the API still accepts it.
     */
    otherPlans: IPatientPurchase[];
    /**
     * This appointment's own catalogue line, to sell the session from when nothing covers
     * it. Null when the appointment names no service or the line has since been pruned,
     * and there is then nothing to open the sale with.
     */
    payNow: IService | null;
}

export interface RescheduleAppointmentRequest {
    /** Defaults to whoever has it now. Set it when a colleague takes the slot over. */
    therapistId?: number;
    startsAt: string;
    endsAt: string;
}

/** A free window in a therapist's diary. */
export interface EmptySlotResponse {
    startTime: string;
    endTime: string;
    durationMinutes: number;
}

/** What `PUT /appointments/{id}/decline` takes. The reason goes into the patient's email. */
export interface DeclineAppointmentRequest {
    reason?: string;
}

/** How many booking requests the signed-in therapist still has to answer. */
export interface IPendingRequestCount {
    count: number;
}

/**
 * The statuses that still occupy a slot.
 *
 * The calendar asks the API for exactly these, which is what keeps a cancelled booking
 * from being drawn over the hour it gave back: the API frees the slot the moment it is
 * called off, so the replacement is a real appointment and the two used to be painted one
 * on top of the other. It is also why the grid needs no overlap handling — the API's own
 * guard refuses two of these overlapping for one therapist, so a single lane is sound.
 *
 * A pending request is one of them: it holds its hour while the therapist decides, and the
 * therapist has to see it in their day in order to decide.
 */
export const OCCUPYING_STATUSES: AppointmentStatus[] = ['SCHEDULED', 'HELD', 'REQUESTED'];

/** Whether an appointment still occupies its slot — what the day view greys out. */
export const occupiesTime = (appointment: IAppointment): boolean =>
    OCCUPYING_STATUSES.includes(appointment.status);

/** Whether it can still be resolved, i.e. it has not reached a terminal state. */
export const isOpen = (appointment: IAppointment): boolean =>
    appointment.status === 'SCHEDULED';

/**
 * How long before its start a request must be answered, in hours. Past it the API refuses a
 * confirmation and declines the request itself.
 *
 * The API's `BookingRequests.CONFIRMATION_LEAD`, restated only for the sentence that tells a
 * patient about it: everything that *decides* on the deadline reads `confirmBy` off the row,
 * which the API computes. Change the two together.
 */
export const REQUEST_CONFIRMATION_LEAD_HOURS = 3;

/** A patient's booking still waiting for its therapist to confirm or decline it. */
export const isPendingRequest = (appointment: IAppointment): boolean =>
    appointment.status === 'REQUESTED';

/**
 * Whether this request's deadline has passed, as of `now` (a wall-clock `YYYY-MM-DDTHH:mm`
 * or longer). Past it the API refuses a confirmation, and the sweep declines the request
 * within minutes — so the button goes before the row does.
 *
 * Compared as strings, which is sound because both sides are zone-less wall-clock in the
 * same fixed-width shape; parsing either into a `Date` would shift it by the viewer's offset.
 */
export const hasLapsed = (appointment: IAppointment, now: string): boolean =>
    isPendingRequest(appointment)
    && !!appointment.confirmBy
    && appointment.confirmBy.slice(0, 16) <= now.slice(0, 16);

/**
 * Whether this viewer may confirm or decline it: only the therapist the patient asked. Not
 * an admin and not a colleague — "confirmed" in the patient's inbox has to mean the person
 * they chose said yes. The API refuses anybody else, so this only decides what to draw.
 */
export const canAnswer = (appointment: IAppointment, viewerId: number): boolean =>
    isPendingRequest(appointment) && appointment.therapistId === viewerId;

/**
 * Whether this viewer may take the request back: the patient who made it, while it is still
 * waiting. Not a cancel button — once confirmed, calling it off is still the clinic's to do.
 */
export const canWithdraw = (appointment: IAppointment, viewerId: number): boolean =>
    isPendingRequest(appointment) && appointment.patientId === viewerId;

/**
 * Whether this viewer may change when it happens.
 *
 * The API decides the same way, from the same facts, so a control drawn here is one the
 * server will honour: **an appointment is moved by the people it involves.** Staff may move
 * anything except a pending request — answering a request with another hour is a decline
 * with a note, not a move; everybody else may move what they are *in*, while it is still
 * ahead of them, booked or requested. A patient's move turns it into a request again. A
 * declined request is final, and a block of leave is nobody's to move from either page — it
 * is removed outright on the calendar that drew it.
 *
 * This keyed on `bookedById` at first, and the screen is what argued it down. Who typed a
 * booking in is a fact about the past rather than about whose appointment it is, so a
 * patient's own list came out split into rows they could move and identical-looking rows
 * they could not — same patient, same therapist, same service — purely by whether they or
 * the desk had made the booking. Nothing on the row could explain the difference, so the
 * inert ones simply read as broken. What makes `patientId` safe is that a move now emails
 * both people; see `AppointmentNotifier.moved` on the server.
 */
export const canReschedule = (
    appointment: IAppointment,
    viewerId: number,
    isStaff: boolean
): boolean =>
    !appointment.timeBlock
    && appointment.status !== 'DECLINED'
    && (isStaff
        ? appointment.status !== 'REQUESTED'
        : appointment.patientId === viewerId
            && (appointment.status === 'SCHEDULED' || appointment.status === 'REQUESTED'));

/**
 * What to call this on screen. A block of leave has no service and no patient, so it
 * needs a label of its own rather than an empty line where the name would be.
 */
export const appointmentTitle = (appointment: IAppointment, blockLabel: string): string =>
    appointment.timeBlock ? blockLabel : (appointment.patientName || '').trim();
